import { createApi } from '@reduxjs/toolkit/query/react';
import { Aggregate, WidgetRequestWithFilters } from '../../models/widget.model';
import { baseQuery } from './base.query';
import { API_PREFIX } from '../../constants';

export interface ChartSeriesResponse {
  groupedList: GroupedList[];
  legendData: LegendDatum[];
}
export interface LegendDatum {
  member: string;
  orderedValues: string[];
}
export interface GroupedList {
  x: string;
  y: number;
  data: Datum[];
}
export interface Datum {
  x: string;
  y: number;
  data?: Datum[];
  tooltip?: string;
  alignmentColor1?: string;
  alignmentColor2?: string;
  alignmentLabel?: string;
  aggregates?: Aggregate[];
  sortOrder: number;
}

export interface AggregateResponse {
  aggregates: Aggregate[];
}

export interface GeographyResponse {
  type: string;
  features: Feature[];
}
interface Feature {
  type: string;
  geometry: Geometry;
  properties: Properties;
}
interface Properties {
  id: string;
  name: string;
  dataCount: number;
}
interface Geometry {
  type: string;
  coordinates: (number[] | number)[][][];
}

export interface ListResponse {
  data: Record<string, any>[];
  total: number;
}

export const chartApi = createApi({
  reducerPath: 'chartApi',
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getChartSeries: builder.query<ChartSeriesResponse, WidgetRequestWithFilters>({
      query: (args) => {
        if (args.request.path === 'alignment-summary/get-alignment-distribution') {
          return {
            url: API_PREFIX.NEXTGEN + args.request.path,
            headers: args.headers,
            method: 'POST',
            body: {
              request: args.request.data,
              filter: args.filters,
              filterLabels: [],
              widget: args.id,
              includeAlignment: args.includeAlignment,
            },
          };
        }
        return {
          url: API_PREFIX.NEXTGEN + args.request.path,
          headers: args.headers,
          method: 'POST',
          body: {
            requests: args.request.data,
            filter: args.filters,
            filterLabels: [],
            widget: args.id,
            includeAlignment: args.includeAlignment,
          },
        };
      },
    }),
    getAggregate: builder.query<AggregateResponse, WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.NEXTGEN + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: args.request.data,
          filter: args.filters,
          filterLabels: [],
          widget: args.id,
          includeAlignment: args.includeAlignment,
        },
      }),
    }),
    getGeoData: builder.query<GeographyResponse, WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.NEXTGEN + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: args.request.data,
          filter: args.filters,
          filterLabels: [],
          widget: args.id,
          includeAlignment: args.includeAlignment,
        },
      }),
    }),
    getList: builder.query<ListResponse, WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.NEXTGEN + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: { pageSize: 20, skip: 0, ...args.request.data },
          filter: args.filters,
          filterLabels: [],
          widget: args.id,
          includeAlignment: args.includeAlignment,
        },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetChartSeriesQuery, useGetListQuery, useGetAggregateQuery, useGetGeoDataQuery } =
  chartApi;
